import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  Button,
  Text,
  Avatar,
  useMessageContext,
  ContextUserType,
} from '@ui/components';
import { X } from 'lucide-react';
import { useClickOutside } from '@ui/hooks';
import { IMessage } from '../../message.typings';
import { IUserReaction, IUserReactionAPI } from '../../message-reactions.typings';

interface MessageReactionDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  message: IMessage | null;
  initialEmoji?: string; // Keep for backward compatibility but not used
  contextUserType: ContextUserType;
}

export const MessageReactionDetailsModal: React.FC<MessageReactionDetailsModalProps> = ({
  visible,
  onClose,
  message,
  contextUserType,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const ref = useClickOutside(onClose);
  const PAGE_SIZE = 20;

  // Get the trigger and state from message context
  const messageState = useMessageContext(contextUserType);
  const trigger = messageState.triggerGetUserReactions;
  const queryState = {
    data: messageState.userReactionsData,
    isLoading: messageState.isLoadingUserReactions || false,
    isFetching: messageState.isFetchingUserReactions || false,
  };

  // Trigger fetch when modal opens
  useEffect(() => {
    if (visible && message?.id && trigger) {
      trigger({ post_id: message.id, page: currentPage, page_size: PAGE_SIZE });
    }
  }, [visible, message?.id, currentPage, trigger]);

  // Reset page when modal opens
  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
    }
  }, [visible]);

  // Transform API data to user list with their emojis
  const usersWithReactions = useMemo(() => {
    if (queryState.data && queryState.data.length > 0) {
      // Use API data
      return queryState.data.map((user: IUserReactionAPI) => ({
        user_id: user.reference_id,
        userName: null,
        displayName: user.display_name,
        avatarURL: user.avatar_url,
        emojis: user.emojis,
      }));
    }

    // Fallback to WebSocket data - transform to flat list
    if (message?.reactions_with_user_details) {
      const userMap = new Map<string, { user: IUserReaction; emojis: string[] }>();

      Object.entries(message.reactions_with_user_details).forEach(([emoji, users]) => {
        users.forEach((user: IUserReaction) => {
          if (userMap.has(user.user_id)) {
            userMap.get(user.user_id)!.emojis.push(emoji);
          } else {
            userMap.set(user.user_id, {
              user,
              emojis: [emoji],
            });
          }
        });
      });

      return Array.from(userMap.values()).map(({ user, emojis }) => ({
        ...user,
        emojis,
      }));
    }

    return [];
  }, [queryState.data, message?.reactions_with_user_details]);

  // Don't show modal if no reactions
  if (usersWithReactions.length === 0 && !queryState.isLoading) {
    return null;
  }

  return (
    <AlertDialog open={visible}>
      <AlertDialogPortal>
        <AlertDialogOverlay className="z-[9999]" />
        <AlertDialogContent
          className="w-full max-w-md sm:max-w-lg rounded-xl p-6 bg-white shadow-lg overflow-y-auto max-h-[80vh] scrollbar-none z-[9999]"
          ref={ref}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <Button
            onClick={onClose}
            type="icon"
            size="smallIcon"
            className="absolute right-0 top-0 bg-transparent shadow-none m-4 border-none hover:bg-gray-100 !h-8 !w-8">
            <X className="h-5 w-5 text-gray-500" />
          </Button>

          <AlertDialogHeader className="text-left">
            <AlertDialogTitle className="text-xl font-bold text-gray-800">
              Reactions
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              See who reacted to this message
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-4">
            {/* Loading state */}
            {queryState.isLoading && currentPage === 1 && (
              <div className="flex justify-center items-center py-8">
                <Text type="m" className="text-gray-500">
                  Loading reactions...
                </Text>
              </div>
            )}

            {/* User List with Reactions */}
            {!queryState.isLoading && usersWithReactions.length > 0 && (
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {usersWithReactions.map((user: IUserReaction) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <Avatar
                          src={user.avatarURL || undefined}
                          displayName={user.displayName || user.userName || 'User'}
                          size="s"
                        />
                        <Text type="m" className="text-sm font-medium text-gray-900 truncate">
                          {user.displayName || user.userName || 'User'}
                        </Text>
                      </div>
                      <div className="flex gap-1 ml-3 shrink-0">
                        {user.emojis?.map((emoji: string, idx: number) => (
                          <span key={idx} className="text-xl">
                            {emoji}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination loading indicator */}
                {queryState.isFetching && currentPage > 1 && (
                  <div className="flex justify-center items-center py-4">
                    <Text type="s" className="text-gray-500">
                      Loading more...
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};
